import React from 'react';
import { Lock, Clock, AlertCircle } from 'lucide-react';

export default function MarketLockBanner({ status, tradesRemaining }) {
  const { isLive, matchLabel, lockedAt, tradingWindowOpen, tradingWindowMessage, currentISTTime } = status || {};

  // Staging mode indicator (subtle, for devs)
  if (status?.stagingMode) {
    return (
      <div style={{
        background: 'rgba(139,92,246,0.07)',
        border: '1px solid rgba(139,92,246,0.25)',
        borderRadius: 10,
        padding: '8px 14px',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#a78bfa', flexShrink: 0 }} />
        <span><strong style={{ color: '#a78bfa' }}>Staging mode</strong> - Trading open 24/7, prices auto-simulate locally with no external API calls. Set <code style={{ background: 'var(--bg-secondary)', padding: '1px 5px', borderRadius: 3 }}>APP_MODE=production</code> in .env to restore the real API flow.</span>
      </div>
    );
  }

  // Nothing to show if trading is fully open
  if (!isLive && tradingWindowOpen) {
    // Show trades-remaining warning only when getting low
    if (tradesRemaining !== undefined && tradesRemaining <= 10 && tradesRemaining > 0) {
      return (
        <div style={{
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 10,
          padding: '10px 16px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <AlertCircle size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
          <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--accent)' }}>{tradesRemaining} trades remaining</strong> this IPL season. Use them wisely.
          </span>
        </div>
      );
    }
    return null;
  }

  if (isLive) {
    const since = lockedAt
      ? new Date(lockedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : '';
    return (
      <div style={{
        background: 'rgba(239,68,68,0.07)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{ flexShrink: 0, paddingTop: 2 }}>
          <span className="live-badge" style={{ fontSize: '0.72rem', padding: '3px 10px' }}>
            <span className="live-dot" /> MATCH LIVE
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', marginBottom: 3 }}>
            Trading paused - {matchLabel}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            Buy and sell are disabled while an IPL match is live.
            Prices update at <strong>7:30 PM</strong> and <strong>11:59 PM IST</strong>.
            Trading reopens when the match ends.
            {since && <span style={{ color: 'var(--text-muted)' }}> · Locked since {since}</span>}
          </div>
        </div>
      </div>
    );
  }

  if (!tradingWindowOpen) {
    return (
      <div style={{
        background: 'rgba(59,130,246,0.07)',
        border: '1px solid rgba(59,130,246,0.25)',
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <Lock size={16} style={{ color: 'var(--blue)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
            Trading window closed
            {currentISTTime && (
              <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={11} /> {currentISTTime}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            {tradingWindowMessage || 'Trading is closed during match hours (3:00 PM - 11:59 PM IST).'}
            {' '}Prices are updated at <strong>7:30 PM</strong> and <strong>11:59 PM</strong>.
            You can browse the market and plan your next trades.
          </div>
        </div>
      </div>
    );
  }

  return null;
}
